/**
 * Shopify mutation hooks. `useShopifyConnection` drives the brand Integrations
 * card (managed-install claim + owner-only uninstall).
 */
import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { ShopifyLifecycleState, ShopifyStatus, ShopifyUninstallResult } from '@discovr/types';
import { createShopifyMutationsApi } from '../api/shopifyMutations';
import { campaignKeys } from '../../campaign-detail/queryKeys';
import { shopifyKeys } from '../queryKeys';
import { SHOPIFY_STATUS_POLL_MS } from './useShopifyQueries';
import { claimFailure, classifyUninstallError, shopifyCopy } from '../../copy';

const api = createShopifyMutationsApi();

const STALE_STATUS_RETRIES = 2;

/** No polling runs while the card reads connected, so a refetch that raced the
 *  backend would leave it stale: look again a couple of times. */
function refetchUntilLeftConnected(queryClient: QueryClient, retriesLeft: number): void {
  setTimeout(() => {
    void queryClient.invalidateQueries({ queryKey: shopifyKeys.status() }).then(() => {
      const state = queryClient.getQueryData<ShopifyStatus>(shopifyKeys.status())?.lifecycle_state;
      if ((state === undefined || state === 'connected') && retriesLeft > 1) {
        refetchUntilLeftConnected(queryClient, retriesLeft - 1);
      }
    });
  }, SHOPIFY_STATUS_POLL_MS);
}

function announceUninstall(
  state: ShopifyLifecycleState | undefined,
  http: ShopifyUninstallResult['status'],
  codesStillOn: number,
): void {
  const note = codesStillOn > 0 ? shopifyCopy.uninstall.codesStillOn(codesStillOn) : '';
  const deleting = () => {
    toast.success([shopifyCopy.lifecycle.deleting, note].filter(Boolean).join(' '));
  };
  const removed = () => {
    toast.success(
      note ? `${shopifyCopy.uninstall.success}. ${note}` : shopifyCopy.uninstall.success,
    );
  };

  switch (state) {
    case 'deleting':
      deleting();
      return;
    case 'removed':
    case 'none':
      removed();
      return;
    case 'uninstalling':
      toast.info(shopifyCopy.lifecycle.uninstallingNoShop);
      return;
    case 'access_lost':
      return;
    default:
      // Refetch failed or still reads connected: report what the HTTP reply confirmed.
      if (http === 'uninstalled') removed();
      else deleting();
  }
}

/** How long to wait for the browser to actually start leaving the page after
 *  a successful submitConnectForm before assuming the navigation was blocked
 *  (a form-action CSP rule, a content blocker swallowing the POST — nothing
 *  that throws) and recovering the button instead of leaving it dead. */
const CONNECT_NAVIGATION_FALLBACK_MS = 4000;

/** Top-level POST to the BE's /shopify/connect. A form submit is the only way
 *  the intent cookie can be set without CORS credentials, since it is set on a
 *  navigation and not an XHR. The browser follows the 302 to the App Store. */
export function submitConnectForm(action: string, intent: string): void {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = action;
  const field = document.createElement('input');
  field.type = 'hidden';
  field.name = 'intent';
  field.value = intent;
  form.appendChild(field);
  document.body.appendChild(form);
  try {
    form.submit();
  } finally {
    // The entry list is built synchronously inside submit(), so the node can
    // go now. finally, not a line after: submit() throwing must still clean
    // up the nonce-bearing form rather than leaking it, and the throw itself
    // still propagates to the caller.
    form.remove();
  }
}

const PICKER_SCOPES: ReadonlySet<string> = new Set([
  'campaign-products',
  'campaign-products-browse',
  'campaign-product-variants',
  'campaign-collections',
  'campaign-creators',
  'campaign-product-status',
]);

/** A claim or uninstall changes what every campaign's Shopify tab and nav item
 *  show; the global 60s staleTime would keep the old state on screen. */
function refreshCampaignScoped(queryClient: QueryClient) {
  // Brand-level status, summary and the spent claim preview are handled by the callers.
  void queryClient.invalidateQueries({
    queryKey: shopifyKeys.all,
    predicate: (q) => {
      const scope = String(q.queryKey[1]);
      return (
        scope === 'conversions' ||
        scope === 'campaign-config' ||
        scope === 'campaign-coupons' ||
        PICKER_SCOPES.has(scope)
      );
    },
  });
  void queryClient.invalidateQueries({ queryKey: [...campaignKeys.all, 'detail'] });
  void queryClient.invalidateQueries({ queryKey: campaignKeys.campaignListAll() });
}

export function useShopifyConnection() {
  const queryClient = useQueryClient();
  const connectFallbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clears a pending "did the browser actually leave?" fallback. Needed on
  // two different signals, for two different reasons:
  //  - pagehide: a real top-level navigation genuinely started. pagehide
  //    fires the instant the browser commits to leaving, independent of how
  //    long the Shopify/OAuth chain downstream takes — so this races only the
  //    gap between submit() returning and the browser starting to unload,
  //    never the full redirect chain. Without it, a slow-but-successful
  //    install could still trip the fallback and show a false error.
  //  - unmount: the merchant left this part of the SPA (no real navigation
  //    happened at all — e.g. they closed the Integrations panel). Without
  //    this, the fallback fires later on whatever screen they're now on, and
  //    toast.error isn't mount-gated the way the mutation's own state is.
  const clearConnectFallback = useCallback(() => {
    if (connectFallbackTimeoutRef.current !== null) {
      clearTimeout(connectFallbackTimeoutRef.current);
      connectFallbackTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    window.addEventListener('pagehide', clearConnectFallback);
    return () => {
      window.removeEventListener('pagehide', clearConnectFallback);
      clearConnectFallback();
    };
  }, [clearConnectFallback]);

  /** Bind a Shopify-managed-install claim token to the logged-in brand.
   *  Called from BrandIntegrationsTab's confirmation dialog, once the merchant
   *  has seen which store the link binds. Never on page load: the token is a
   *  bearer credential, so binding without a click was exploitable. */
  const claim = useMutation({
    mutationFn: (token: string) => api.claimInstall(token),
    onSuccess: async () => {
      void queryClient.invalidateQueries({ queryKey: shopifyKeys.summary() });
      refreshCampaignScoped(queryClient);
      // A claim binds a (possibly different) store to the brand — the
      // "campaigns affected by uninstall" list from any PRIOR connection is
      // now meaningless; refetch it fresh for the newly bound store.
      void queryClient.invalidateQueries({ queryKey: shopifyKeys.uninstallImpact() });
      toast.success(shopifyCopy.claim.success);
      // Awaited so the dialog closes onto the new status, not a flash of the old one.
      await queryClient.invalidateQueries({ queryKey: shopifyKeys.status() });
    },
    // Same mapping the dialog uses, deliberately: React Query skips per-call
    // callbacks after unmount, so without this a merchant who navigates away
    // mid-claim is told nothing at all. Identical copy means the two surfaces
    // can never contradict each other, which they did when this used the
    // generic errorMessage().
    onError: (err: Error) => toast.error(claimFailure(err).message),
    // Whatever happened, re-read the connection. A claim can commit server-side
    // and still fail here (a dropped connection, the request timeout), and
    // without this the card keeps saying "not connected" for a store that is.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: shopifyKeys.status() });
    },
  });

  /** Start a brand-bound install. On success the browser leaves the page, so
   *  there is nothing to invalidate. The failure copy is the claim copy, which
   *  already maps 403 to "only an owner can connect" and 409 to "uninstall
   *  first". */
  const connect = useMutation({
    mutationFn: () => api.createConnectIntent(),
    onSuccess: ({ intent, submit_url }) => {
      // onSuccess throwing is never routed to onError (that's for mutationFn
      // rejections only), so a failure here — form.submit() itself throwing —
      // must be handled right here, or the button disables on isPending/
      // isSuccess and never recovers: a dead control with no feedback.
      try {
        submitConnectForm(submit_url, intent);
      } catch {
        toast.error(shopifyCopy.claim.claimUnavailable);
        connect.reset();
        return;
      }
      // submit() not throwing doesn't mean the browser is actually leaving —
      // a form-action CSP rule or a content blocker can swallow the POST with
      // nothing to catch. If we're still here after a few seconds, recover
      // instead of leaving the merchant on a permanently disabled button.
      // clearConnectFallback cancels this the moment pagehide fires (a real
      // navigation started) or this hook unmounts — see its own comment.
      connectFallbackTimeoutRef.current = setTimeout(() => {
        connectFallbackTimeoutRef.current = null;
        toast.error(shopifyCopy.claim.claimUnavailable);
        connect.reset();
      }, CONNECT_NAVIGATION_FALLBACK_MS);
    },
    onError: (err: Error) => toast.error(claimFailure(err).message),
  });

  const uninstall = useMutation({
    mutationFn: (opts: { turnOffCodes: boolean; continueAnyway: boolean }) => api.uninstall(opts),
    onSuccess: async (result) => {
      void queryClient.invalidateQueries({ queryKey: shopifyKeys.summary() });
      refreshCampaignScoped(queryClient);
      queryClient.removeQueries({ queryKey: shopifyKeys.uninstallImpact() });
      await queryClient.invalidateQueries({ queryKey: shopifyKeys.status() });
      const state = queryClient.getQueryData<ShopifyStatus>(shopifyKeys.status())?.lifecycle_state;
      announceUninstall(state, result.status, (result.code_failures ?? []).length);
      if (state === undefined || state === 'connected') {
        refetchUntilLeftConnected(queryClient, STALE_STATUS_RETRIES);
      }
    },
    // Awaited so the error only surfaces once the refetched status is in.
    onError: async (err) => {
      if (classifyUninstallError(err) === 'unexpected') {
        console.error('[Shopify] uninstall failed unexpectedly', err);
      }
      await queryClient.invalidateQueries({ queryKey: shopifyKeys.status() });
    },
  });

  return { claim, connect, uninstall };
}
